import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';
import { Button, Chip } from '@progress/kendo-react-buttons';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { Error, Label } from '@progress/kendo-react-labels';
import { Loader } from '@progress/kendo-react-indicators';
import { Notification } from '@progress/kendo-react-notification';
import { ApplicationDispatch, getDownPaymentHistories } from '../../../../store/application/types';
import {
  refreshDownPaymentCardCharges,
  runDownPaymentCardAction,
  useDownPaymentCardCharges,
} from '../../../../Data/DownPaymentCardData';
import {
  CARD_ACTION_LABELS,
  CardAction,
  cardEnding,
  chargesToActOn,
  DownPaymentCardCharge,
  money,
} from '../../../../payments/downPaymentCards';
import { refusalText } from '../../../../payments/cardAnswer';
import styles from './_card-payments-window.module.scss';

interface CardPaymentsWindowProps {
  applicationId: string;
  onClose(): void;
}

/** The two actions that ask for something first: the AMS receipt, or a note saying what was done. */
type PromptAction = Extract<CardAction, 'RecordReceipt' | 'Resolve'>;

interface Prompt {
  ledgerId: string;
  action: PromptAction;
  text: string;
  /** Save was pressed with nothing typed. */
  showRequired: boolean;
}

const PROMPTS: Record<PromptAction, { label: string; required: string }> = {
  RecordReceipt: { label: 'AMS receipt', required: 'Enter the AMS receipt number.' },
  Resolve: { label: 'Note', required: 'Enter a note saying what was done.' },
};

const ACTION_LOST = 'No answer came back. Use Check status to see where it stands.';

/**
 * Card payments (LOS card down payments A13): the application's card down payments that AMS doesn't have yet, or whose
 * card reversal is waiting or failed, with what can be done about each (A10, A11).
 */
export const CardPaymentsWindow: React.FC<CardPaymentsWindowProps> = ({ applicationId, onClose }) => {
  const dispatch = useDispatch<ApplicationDispatch>();
  const charges = useDownPaymentCardCharges(applicationId);
  const rows = chargesToActOn(charges.data);
  const [busyLedgerId, setBusyLedgerId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; isError: boolean } | null>(null);
  const [prompt, setPrompt] = useState<Prompt | null>(null);

  const run = async (charge: DownPaymentCardCharge, action: CardAction, body?: { receipt?: string; note?: string }) => {
    setBusyLedgerId(charge.ledgerId);
    setNotice(null);
    try {
      const answer = await runDownPaymentCardAction(charge.ledgerId, action, body);
      if (answer.kind === 'ok') {
        setNotice({ text: answer.data.message || answer.data.charge?.message || answer.data.charge?.statusText || '', isError: false });
        setPrompt(null);
      } else {
        setNotice({ text: answer.kind === 'refused' ? refusalText(answer) : ACTION_LOST, isError: true });
      }
    } catch {
      setNotice({ text: ACTION_LOST, isError: true });
    } finally {
      setBusyLedgerId(null);
      void refreshDownPaymentCardCharges(applicationId);
      dispatch(getDownPaymentHistories(applicationId));
    }
  };

  const choose = (charge: DownPaymentCardCharge, action: CardAction) => {
    if (action === 'RecordReceipt' || action === 'Resolve') {
      setPrompt({ ledgerId: charge.ledgerId, action, text: '', showRequired: false });
      return;
    }
    setPrompt(null);
    void run(charge, action);
  };

  const save = (charge: DownPaymentCardCharge) => {
    if (!prompt) return;
    const text = prompt.text.trim();
    if (!text) {
      setPrompt({ ...prompt, showRequired: true });
      return;
    }
    void run(charge, prompt.action, prompt.action === 'RecordReceipt' ? { receipt: text } : { note: text });
  };

  const renderPrompt = (charge: DownPaymentCardCharge) => {
    if (!prompt || prompt.ledgerId !== charge.ledgerId) return null;
    const { label, required } = PROMPTS[prompt.action];
    const id = `card-payment-${prompt.action}-${charge.ledgerId}`;
    const busy = busyLedgerId === charge.ledgerId;
    return (
      <div className={styles.prompt}>
        <Label editorId={id}>{label}</Label>
        {prompt.action === 'RecordReceipt'
          ? <Input
              id={id}
              value={prompt.text}
              disabled={busy}
              autoComplete="off"
              onChange={(event) => setPrompt({ ...prompt, text: String(event.value ?? ''), showRequired: false })}
            />
          : <TextArea
              id={id}
              value={prompt.text}
              disabled={busy}
              rows={3}
              onChange={(event) => setPrompt({ ...prompt, text: String(event.value ?? ''), showRequired: false })}
            />
        }
        {prompt.showRequired && <Error>{required}</Error>}
        <div className={styles.actions}>
          <Button themeColor="primary" disabled={busy} onClick={() => save(charge)}>Save</Button>
          <Button disabled={busy} onClick={() => setPrompt(null)}>Cancel</Button>
        </div>
      </div>
    );
  };

  const renderRow = (charge: DownPaymentCardCharge) => {
    const busy = busyLedgerId === charge.ledgerId;
    const card = cardEnding(charge);
    return (
      <li key={charge.ledgerId} className={styles.row}>
        <div className={styles.summary}>
          <span className={styles.amount}>{money(charge.amount)}</span>
          <span>{new Date(charge.createdDate).toLocaleString()}</span>
          {card && <span>{card}</span>}
          <Chip text={charge.statusText} themeColor={charge.unfinished ? 'warning' : 'error'} size="small" />
        </div>
        {charge.message && <div className={styles.message}>{charge.message}</div>}
        {charge.actions.length > 0 &&
          <div className={styles.actions}>
            {charge.actions.map((action) =>
              <Button key={action} disabled={busyLedgerId !== null} onClick={() => choose(charge, action)}>
                {CARD_ACTION_LABELS[action] ?? action}
              </Button>
            )}
            {busy && <Loader size="small" type="pulsing" />}
          </div>
        }
        {renderPrompt(charge)}
      </li>
    );
  };

  return (
    <Window
      title="Card payments"
      modal={true}
      initialWidth={Math.min(680, Math.round(window.innerWidth * 0.95))}
      initialHeight={Math.min(560, Math.round(window.innerHeight * 0.9))}
      minimizeButton={() => <span></span>}
      onClose={onClose}
      className={styles.window}
    >
      {notice &&
        <Notification type={{ style: notice.isError ? 'error' : 'info', icon: true }} className={styles.notice}>
          {notice.text}
        </Notification>
      }
      {charges.status === 'loading' && !charges.data
        ? <Loader size="medium" type="pulsing" />
        : charges.status === 'error' && !charges.data
          ? <Error>{charges.error instanceof globalThis.Error ? charges.error.message : 'Card payments could not be read.'}</Error>
          : rows.length === 0
            ? <p className={styles.empty}>No card payments need action.</p>
            : <ul className={styles.list}>{rows.map(renderRow)}</ul>
      }
      <WindowActionsBar>
        <Button onClick={onClose}>Close</Button>
      </WindowActionsBar>
    </Window>
  );
};
